import { Button } from '@creditornot/cb-button';
import { cbSemanticCssVars, Typography as T } from '@creditornot/cb-ingredients';
import { ModalContent, ModalFooter, ModalHeader } from '@creditornot/cb-modal';
import { useMutation } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import React, { useCallback } from 'react';
import toast from 'react-hot-toast';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { APIErrorNotification, WoltModal } from 'common/components';
import { useImmediatePublishSharedMenuTracking } from 'modules/menu/hooks/telemetryHooks';
import { publishSharedMenu } from 'modules/shared-menu/api';
import { useScopeContext } from 'modules/shared-menu/contexts/ScopeContext';
import { useLiveSelectionData } from 'modules/shared-menu/hooks/useLiveSelectionData';
import { useSharedMenuData } from 'modules/shared-menu/hooks/useSharedMenuData';

import { useSelectionData } from '../../../hooks/useSelectionData';

type Props = {
  onClose: () => void;
  isOpen: boolean;
  selectionId: string;
};

const Root = styled(WoltModal)`
  min-width: 520px;
  border-radius: 8px;
  background-color: ${cbSemanticCssVars.surfaceMain};
  display: flex;
  flex-direction: column;
  overflow: visible;
`;

const StyledFooter = styled(ModalFooter)`
  display: flex;
  flex-direction: column;
`;

const Buttons = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: flex-end;
`;

const ConfirmButton = styled(Button).attrs({ variant: 'blue', size: 'small' })``;

const CancelButton = styled(Button).attrs({ variant: 'lightBlue', size: 'small' })`
  margin-inline-end: 16px;
`;

export const ImmediatePublishModal = ({ isOpen, onClose, selectionId }: Props) => {
  const scope = useScopeContext();
  const { selection, updateSelectionData } = useSelectionData();
  const { resetLiveSelectionQuery } = useLiveSelectionData({ enabled: false, selectionId });
  const { updateSharedMenu } = useSharedMenuData({ scope });

  const { mutate, isLoading, error, reset } = useMutation(async () => publishSharedMenu(selectionId, null), {
    onSuccess: () => {
      const currentDateTimeIso = DateTime.now().toUTC().toISO();
      const updatedSelection = {
        ...selection,
        draft: undefined,
        version: {
          ...selection.version,
          updated_at: currentDateTimeIso,
          number: selection.version.number + 1,
        },
      };
      updateSelectionData({
        actionType: 'updateSelection',
        payload: updatedSelection,
        preventManualStatusUpdate: true,
      });
      updateSharedMenu(updatedSelection);
      resetLiveSelectionQuery();
      toast.success(<FormattedMessage id="listing-manager.selections.editor.publish.immediate.success" />);
      handleClose();
    },
  });

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [reset, onClose]);

  const trackMenuPublishing = useImmediatePublishSharedMenuTracking(selection.id);

  return (
    <Root isOpen={isOpen} onRequestClose={handleClose}>
      <ModalHeader onCloseClick={handleClose}>
        <FormattedMessage id="shared-menu.publish.now.label" />
      </ModalHeader>
      <ModalContent>
        <T.Body3>
          <FormattedMessage id="shared-menu.publish.now.description" />
        </T.Body3>
      </ModalContent>
      <StyledFooter>
        {!!error && <APIErrorNotification style={{ width: '100%', marginBlockEnd: '16px' }} error={error} />}
        <Buttons>
          <CancelButton onClick={handleClose} disabled={isLoading}>
            <FormattedMessage id="state.cancel" />
          </CancelButton>
          <ConfirmButton
            type="submit"
            onClick={() => {
              mutate();
              trackMenuPublishing();
            }}
            disabled={isLoading}
            loading={isLoading}
          >
            <FormattedMessage id="shared-menu.publish.now.label" />
          </ConfirmButton>
        </Buttons>
      </StyledFooter>
    </Root>
  );
};
